import { LocationAddressFilterType } from '@/entities/location';
import type {
  ConsignmentUpdateRequest,
  ConsignmentCreateRequest,
  ArrivalTypeCode,
  ServiceTypeCode,
  ConsignmentSummary,
  ConsignmentUpdateParams,
  UpdateConsignmentWithAttachmentsData
} from '@/entities/consignments';
import type { ServiceRequestDetail, ServiceRequestServiceTypeCode } from '@/entities/service-request';
import type { AttachmentsData } from '@/features/consignment-attachment-list';

import type { ConsignmentFormValues } from '../model/validation';

// Type alias for strict typing
type StartingPointType =
  | typeof LocationAddressFilterType.CUSTOMER_ADDRESS
  | typeof LocationAddressFilterType.ARRIVAL_PORT_TRANSSHIPMENT
  | typeof LocationAddressFilterType.ARRIVAL_DELIVERY_OR_PICKUP_AT_SITE;

type ServiceFilterType =
  | typeof LocationAddressFilterType.CUSTOMER_ADDRESS
  | typeof LocationAddressFilterType.WAREHOUSE_PICKUP
  | typeof LocationAddressFilterType.STORAGE;

// Type guards for validation
function isValidStartingPointType(type: LocationAddressFilterType): type is StartingPointType {
  const validTypes: LocationAddressFilterType[] = [
    LocationAddressFilterType.CUSTOMER_ADDRESS,
    LocationAddressFilterType.ARRIVAL_PORT_TRANSSHIPMENT,
    LocationAddressFilterType.ARRIVAL_DELIVERY_OR_PICKUP_AT_SITE
  ];
  return validTypes.includes(type);
}

function isValidServiceType(type: LocationAddressFilterType): type is ServiceFilterType {
  const validTypes: LocationAddressFilterType[] = [
    LocationAddressFilterType.CUSTOMER_ADDRESS,
    LocationAddressFilterType.WAREHOUSE_PICKUP,
    LocationAddressFilterType.STORAGE
  ];
  return validTypes.includes(type);
}

/**
 * Maps backend arrivalType.id to LocationAddressFilterType enum value
 * @param code - The arrivalType code from the backend
 * @returns The corresponding LocationAddressFilterType or undefined if not found
 */
export const mapArrivalTypeToFilterType = (code?: string): LocationAddressFilterType | undefined => {
  const mapping: Record<ArrivalTypeCode, LocationAddressFilterType> = {
    C: LocationAddressFilterType.CUSTOMER_ADDRESS,
    P: LocationAddressFilterType.ARRIVAL_PORT_TRANSSHIPMENT,
    X: LocationAddressFilterType.ARRIVAL_DELIVERY_OR_PICKUP_AT_SITE,
    I: LocationAddressFilterType.ARRIVAL_DELIVERY_OR_PICKUP_AT_SITE // Mapping 'I' to same as 'X' as fallback
  };
  return code ? mapping[code as ArrivalTypeCode] : undefined;
};

/**
 * Maps backend serviceType.id to LocationAddressFilterType enum value
 * @param code - The serviceType code from the backend
 * @returns The corresponding LocationAddressFilterType or undefined if not found
 */
export const mapServiceTypeToFilterType = (code?: string): LocationAddressFilterType | undefined => {
  const mapping: Record<ServiceTypeCode, LocationAddressFilterType> = {
    D: LocationAddressFilterType.CUSTOMER_ADDRESS,
    P: LocationAddressFilterType.WAREHOUSE_PICKUP,
    S: LocationAddressFilterType.STORAGE
  };
  return code ? mapping[code as ServiceTypeCode] : undefined;
};

/**
 * Maps LocationAddressFilterType enum value to backend arrivalType.id
 * @param type - The LocationAddressFilterType enum value
 * @returns The corresponding arrivalType code or undefined if not found
 */
export const mapFilterTypeToArrivalCode = (type?: LocationAddressFilterType): ArrivalTypeCode | undefined => {
  const mapping: Partial<Record<LocationAddressFilterType, ArrivalTypeCode>> = {
    [LocationAddressFilterType.CUSTOMER_ADDRESS]: 'C',
    [LocationAddressFilterType.ARRIVAL_PORT_TRANSSHIPMENT]: 'P',
    [LocationAddressFilterType.ARRIVAL_DELIVERY_OR_PICKUP_AT_SITE]: 'X'
  };
  return type ? mapping[type] : undefined;
};

/**
 * Maps LocationAddressFilterType enum value to backend serviceType.id
 * @param type - The LocationAddressFilterType enum value
 * @returns The corresponding serviceType code or undefined if not found
 */
export const mapFilterTypeToServiceCode = (type?: LocationAddressFilterType): ServiceTypeCode | undefined => {
  const mapping: Partial<Record<LocationAddressFilterType, ServiceTypeCode>> = {
    [LocationAddressFilterType.CUSTOMER_ADDRESS]: 'D',
    [LocationAddressFilterType.WAREHOUSE_PICKUP]: 'P',
    [LocationAddressFilterType.STORAGE]: 'S'
  };
  return type ? mapping[type] : undefined;
};

/**
 * Maps transport mode description to planned mode enum value
 * @param transportMode - The transport mode description from the backend
 * @returns The corresponding planned mode or 'none' if not found
 */
export const mapTransportModeToPlannedMode = (transportMode?: string): 'none' | 'air' | 'road' | 'sea' | 'rail' => {
  if (!transportMode) return 'none';
  const mode = transportMode.toLowerCase();
  if (mode.includes('air')) return 'air';
  if (mode.includes('road')) return 'road';
  if (mode.includes('sea')) return 'sea';
  if (mode.includes('rail')) return 'rail';
  return 'none';
};

/**
 * Transforms form values to API request format for updating a consignment
 * @param data The form values from the consignment edit form
 * @returns The API request object for updating the consignment
 */
export const transformFormToRequest = (data: ConsignmentFormValues): ConsignmentUpdateRequest => {
  const updateRequest: ConsignmentUpdateRequest = {
    ownersRef: data.ownersReference,
    remarks: data.remarks,
    cmrLinks: data.cmrLinks ?? undefined,
    senderContactId: data.senderContact,
    receiverContactId: data.receiverContact,
    invoiceContactId: data.clearingAgentContact,
    // Add date fields if they exist
    ...(data.expectedArrivalDate && { loadDate: new Date(data.expectedArrivalDate).getTime() }),
    ...(data.onSiteDate && { dispatchPlanDate: new Date(data.onSiteDate).getTime() }),
    // Reverse mapping: form enum values → API codes
    ...(data.startingPointType && { arrivalTypeCode: mapFilterTypeToArrivalCode(data.startingPointType) }),
    ...(data.serviceType && { serviceTypeCode: mapFilterTypeToServiceCode(data.serviceType) }),
    // Location IDs (direct string)
    ...(data.arrivingTo && { arrivalLocationId: data.arrivingTo }),
    ...(data.serviceDestination && { destinationLocationId: data.serviceDestination }),
    // Transporter number (already string in API)
    ...(data.missionTransporterNumber != null && { transporterNumber: String(data.missionTransporterNumber) }),
    // Storage duration
    ...(data.durationInDays != null && { storageDuration: Number(data.durationInDays) })
  };

  return updateRequest;
};

/**
 * Transforms form values to API request format for creating a consignment.
 * Reuses the shared field mapping and enforces the fields the create endpoint
 * requires (arrival location + sender contact).
 * @param data The form values from the consignment form
 * @returns The API request object for creating the consignment
 */
export const transformFormToCreateRequest = (data: ConsignmentFormValues): ConsignmentCreateRequest => {
  return {
    ...transformFormToRequest(data),
    arrivalLocationId: data.arrivingTo,
    senderContactId: data.senderContact ?? '',
    priority: data.priority
  };
};

/**
 * Maps an online SRF service type to the backend arrival/service codes the consignment uses.
 * Mirrors the backend ServiceRequestActionServiceImpl.moveToConsignment switch.
 */
const SRF_SERVICE_TYPE_TO_CODES: Record<
  ServiceRequestServiceTypeCode,
  { arrival: ArrivalTypeCode; service: ServiceTypeCode }
> = {
  S: { arrival: 'X', service: 'S' },
  SP: { arrival: 'C', service: 'S' },
  N: { arrival: 'X', service: 'P' },
  T: { arrival: 'C', service: 'D' }
};

/**
 * Best-effort prefill of the create-consignment form from a selected service request.
 * Organisations are preselected by id; storage locations resolve directly; transport
 * (T) addresses and all contacts are left for the user (shown as SRF hints in the cards).
 * @param srf The full service request detail
 * @returns Partial form values to merge into the create form defaults
 */
export const transformSrfDetailToForm = (srf: ServiceRequestDetail): Partial<ConsignmentFormValues> => {
  const prefill: Partial<ConsignmentFormValues> = {};

  if (srf.ownersReference) {
    prefill.ownersReference = srf.ownersReference;
  }

  // Organisations: preselect by id when the SRF resolved one
  if (srf.senderOrgId != null) {
    prefill.senderOrganization = String(srf.senderOrgId);
  }
  const receiverOrgId = srf.receiverSameAsSender ? srf.senderOrgId : srf.receiverOrgId;
  if (receiverOrgId != null) {
    prefill.receiverOrganization = String(receiverOrgId);
  }
  if (srf.clearingOrgId != null) {
    prefill.clearingAgent = String(srf.clearingOrgId);
  }

  // Service-type-driven selects + locations
  const codes = srf.serviceTypeCode ? SRF_SERVICE_TYPE_TO_CODES[srf.serviceTypeCode] : undefined;
  if (codes) {
    const startingPointType = mapArrivalTypeToFilterType(codes.arrival);
    const serviceType = mapServiceTypeToFilterType(codes.service);
    if (startingPointType && isValidStartingPointType(startingPointType)) {
      prefill.startingPointType = startingPointType;
    }
    if (serviceType && isValidServiceType(serviceType)) {
      prefill.serviceType = serviceType;
    }
  }

  // Storage service types carry a concrete location; transport (T) does not.
  // Backend mapping: S/N use storage as both arrival + destination; SP uses storage as destination only.
  if (srf.storageLocationId) {
    if (srf.serviceTypeCode === 'S' || srf.serviceTypeCode === 'N') {
      prefill.arrivingTo = srf.storageLocationId;
    }
    if (srf.serviceTypeCode === 'S' || srf.serviceTypeCode === 'N' || srf.serviceTypeCode === 'SP') {
      prefill.serviceDestination = srf.storageLocationId;
    }
  }

  // Pickup date: ready-to-load for transport, otherwise storage start
  const pickupMs = srf.readyToLoadDate ?? srf.storageDateFrom;
  if (pickupMs != null) {
    prefill.expectedArrivalDate = new Date(pickupMs).toISOString();
  }

  // Instructions
  if (srf.arrivalInstructions) {
    prefill.instructions = srf.arrivalInstructions;
  }
  if (srf.finalInstructions) {
    prefill.serviceInstructions = srf.finalInstructions;
  }

  return prefill;
};

/**
 * Transforms API response to form values for the consignment edit form
 * @param consignmentData The API response data for a consignment
 * @returns The form values populated with the API response data
 */
export const transformResponseToForm = (consignmentData: ConsignmentSummary): Partial<ConsignmentFormValues> => {
  const mappedStartingPointType = mapArrivalTypeToFilterType(consignmentData.arrivalType?.id);
  const mappedServiceType = mapServiceTypeToFilterType(consignmentData.serviceType?.id);

  const formDefaults: Partial<ConsignmentFormValues> = {
    ownersReference: consignmentData.ownersRef || '',
    remarks: consignmentData.remarks || '',
    cmrLinks: consignmentData.cmrLinks || '',
    status: consignmentData.statusCode,
    priority:
      consignmentData.dispatchPriorityName?.toLowerCase() === 'urgent'
        ? 'urgent'
        : consignmentData.dispatchPriorityName?.toLowerCase() === 'high'
          ? 'high'
          : 'normal',
    plannedMode: mapTransportModeToPlannedMode(consignmentData.transportModeDescription),
    transportUnitQuantity: consignmentData.numberOfPallets,
    senderOrganization: consignmentData.senderContact?.organization?.id
      ? String(consignmentData.senderContact.organization.id)
      : undefined,
    senderContact: consignmentData.senderContact?.contact?.id
      ? String(consignmentData.senderContact.contact.id)
      : undefined,
    receiverOrganization: consignmentData.receiverContact?.organization?.id
      ? String(consignmentData.receiverContact.organization.id)
      : undefined,
    receiverContact: consignmentData.receiverContact?.contact?.id
      ? String(consignmentData.receiverContact.contact.id)
      : undefined,
    clearingAgent: consignmentData.clearingAgent?.organization?.id
      ? String(consignmentData.clearingAgent.organization.id)
      : undefined,
    clearingAgentContact: consignmentData.clearingAgent?.contact?.id
      ? String(consignmentData.clearingAgent.contact.id)
      : undefined,

    // Location fields (direct IDs)
    ...(consignmentData.arrivalLocation?.id && { arrivingTo: consignmentData.arrivalLocation.id }),
    ...(consignmentData.destinationLocation?.id && { serviceDestination: consignmentData.destinationLocation.id }),

    // Transporter number
    ...(consignmentData.transporterNumber && { missionTransporterNumber: consignmentData.transporterNumber }),

    // Storage duration
    ...(consignmentData.storageDuration != null && { durationInDays: consignmentData.storageDuration }),

    // On-site date: convert Unix ms timestamp to ISO 8601 string
    ...(consignmentData.estimatedDispatchTime && {
      onSiteDate: new Date(consignmentData.estimatedDispatchTime).toISOString()
    })
  };

  // Only add type fields if they match expected values
  if (mappedStartingPointType && isValidStartingPointType(mappedStartingPointType)) {
    formDefaults.startingPointType = mappedStartingPointType as StartingPointType;
  }

  if (mappedServiceType && isValidServiceType(mappedServiceType)) {
    formDefaults.serviceType = mappedServiceType as ServiceFilterType;
  }

  return formDefaults;
};

/**
 * Builds the complete update request including attachments data
 * @param formData The form values from the consignment edit form
 * @param attachments Optional attachments data provider (only needs getAttachmentFiles method)
 * @returns Object containing params, update request and attachments data
 */
export const buildUpdateRequest = (
  consignmentId: string,
  projectId: string | number,
  siteId: string | number,
  formData: ConsignmentFormValues,
  attachments?: Pick<AttachmentsData, 'getAttachmentFiles'> | null
) => {
  const params: ConsignmentUpdateParams = {
    id: consignmentId,
    projectId: Number(projectId),
    siteId: Number(siteId)
  };

  const updateRequest = transformFormToRequest(formData);

  const attachmentFiles = attachments?.getAttachmentFiles();
  const attachmentsData: UpdateConsignmentWithAttachmentsData | undefined = attachmentFiles
    ? {
        files: attachmentFiles.files,
        attachmentType: attachmentFiles.attachmentType,
        comment: attachmentFiles.comment
      }
    : undefined;

  return {
    params,
    updateRequest,
    attachmentsData,
    hasLocalAttachments: !!attachmentFiles?.files?.length
  };
};

export type Helper<T> = T extends object ? { [P in keyof T]: Helper<T[P]> | undefined } : T;
